import { createContext, useContext, useEffect, useState, useRef, useCallback, type ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { User, Session } from "@supabase/supabase-js";
import { isUserIdle } from "@/lib/idleDetector";
import { broadcastChatUserLogout, dispatchChatUserLogout } from "@/lib/onlineUserLogout";
import { clearStoredGuestName, markGuestManualLogout, shouldSuppressAnonymousSessionAfterLogout } from "@/lib/guestEntry";
import { setCachedBootData, getCachedBootData } from "@/lib/chatBootCache";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signOut: () => Promise<void>;
}

const AUTH_CACHE_KEY = "chat-auth-cache";
const AUTH_INIT_TIMEOUT_MS = 8000;
const PRESENCE_ONLINE_STALE_MS = 4 * 60_000;

function readCachedAuth() {
  if (typeof window === "undefined") return null;

  try {
    const raw = window.localStorage.getItem(AUTH_CACHE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as { user?: User | null; session?: Session | null };
    if (!parsed?.user || !parsed?.session) return null;

    return {
      user: parsed.user,
      session: parsed.session,
    };
  } catch {
    return null;
  }
}

function writeCachedAuth(session: Session | null) {
  if (typeof window === "undefined") return;

  try {
    if (!session?.user) {
      window.localStorage.removeItem(AUTH_CACHE_KEY);
      return;
    }

    window.localStorage.setItem(
      AUTH_CACHE_KEY,
      JSON.stringify({
        user: session.user,
        session,
      })
    );
  } catch {
    // ignore storage issues
  }
}

async function getSessionWithTimeout(timeoutMs: number): Promise<Session | null> {
  const sessionPromise = supabase.auth
    .getSession()
    .then(({ data: { session } }) => session)
    .catch(() => null);

  const timeoutPromise = new Promise<Session | null>((resolve) => {
    const timeout = setTimeout(() => {
      clearTimeout(timeout);
      resolve(null);
    }, timeoutMs);
  });

  return Promise.race([sessionPromise, timeoutPromise]);
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  loading: true,
  signOut: async () => {},
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const cachedAuth = readCachedAuth();
  const [user, setUser] = useState<User | null>(cachedAuth?.user ?? null);
  const [session, setSession] = useState<Session | null>(cachedAuth?.session ?? null);
  const [loading, setLoading] = useState(!cachedAuth);
  const heartbeatRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const lastPresenceWriteRef = useRef<{ userId: string; online: boolean; at: number } | null>(null);

  const updatePresence = useCallback(async (userId: string, online: boolean) => {
    const now = Date.now();
    const lastWrite = lastPresenceWriteRef.current;
    if (online && lastWrite?.userId === userId && lastWrite.online && now - lastWrite.at < PRESENCE_ONLINE_STALE_MS) {
      return;
    }

    lastPresenceWriteRef.current = { userId, online, at: now };

    try {
      await supabase
        .from("profiles")
        .update({ is_online: online, last_seen: new Date(now).toISOString() })
        .eq("user_id", userId);
    } catch {}
  }, []);

  useEffect(() => {
    let isMounted = true;
    let initialSessionHandled = false;

    const applySession = (nextSession: Session | null, event?: string) => {
      if (!isMounted) return;

      if (nextSession?.user?.is_anonymous && shouldSuppressAnonymousSessionAfterLogout()) {
        supabase.auth.signOut();
        return;
      }

      // CRITICAL: Don't clear a cached session on INITIAL_SESSION with null.
      // This happens when the stored token is expired and Supabase hasn't
      // refreshed it yet. Wait for TOKEN_REFRESHED or SIGNED_OUT instead.
      // Re-read localStorage live — the closure cachedAuth could be stale
      // after a sign-out from another component.
      if (
        !nextSession &&
        event === "INITIAL_SESSION" &&
        readCachedAuth()?.session
      ) {
        // Keep cached session alive, just mark loading as done
        setLoading(false);
        return;
      }

      // On explicit SIGNED_OUT, always clear
      // On TOKEN_REFRESHED or SIGNED_IN, always apply
      setSession(nextSession);
      setUser(nextSession?.user ?? null);
      writeCachedAuth(nextSession);
      setLoading(false);
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, nextSession) => {
        // Skip the initial event from onAuthStateChange if getSession already handled it
        if (event === "INITIAL_SESSION" && initialSessionHandled) return;
        applySession(nextSession, event);
      }
    );

    getSessionWithTimeout(AUTH_INIT_TIMEOUT_MS)
      .then((nextSession) => {
        initialSessionHandled = true;
        if (nextSession) {
          applySession(nextSession, "GET_SESSION");
        } else if (!cachedAuth?.session) {
          // Only clear if we have no cache to fall back on
          applySession(null, "GET_SESSION");
        } else {
          // We have cache but getSession returned null — token may be refreshing
          // Just mark loading done, keep cache, wait for TOKEN_REFRESHED event
          if (isMounted) setLoading(false);
        }
      })
      .catch(() => {
        if (!cachedAuth?.session) {
          applySession(null, "GET_SESSION_ERROR");
        } else {
          if (isMounted) setLoading(false);
        }
      });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  // Presence heartbeat — use user.id (stable string) as dep to avoid false→true flip
  const userIdRef = useRef<string | null>(null);
  const presenceCleanupDone = useRef(false);
  useEffect(() => {
    const uid = user?.id ?? null;
    // Skip if same user (avoid cleanup→re-run on object ref change)
    if (uid === userIdRef.current) return;
    
    // Clean up previous user (only once)
    if (userIdRef.current && !uid && !presenceCleanupDone.current) {
      presenceCleanupDone.current = true;
      updatePresence(userIdRef.current, false);
    }
    userIdRef.current = uid;
    presenceCleanupDone.current = false;
    
    if (!uid) {
      if (heartbeatRef.current) {
        clearInterval(heartbeatRef.current);
        heartbeatRef.current = null;
      }
      return;
    }

    // Set online immediately
    updatePresence(uid, true);

    // Sparse profile heartbeat; site_presence handles live chat presence.
    heartbeatRef.current = setInterval(() => {
      if (isUserIdle()) return; // skip heartbeat when user is idle
      updatePresence(uid, true);
    }, 600_000);

    let offlineSignalSent = false;

    const sendOfflineBeacon = () => {
      if (offlineSignalSent) return;
      offlineSignalSent = true;

      const beaconUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/beacon-offline`;
      const body = JSON.stringify({ user_id: uid });
      navigator.sendBeacon?.(beaconUrl, new Blob([body], { type: "application/json" }));
      dispatchChatUserLogout(uid);
    };

    // On tab close / page hide — set user offline via edge function (no auth headers needed)
    const handleBeforeUnload = () => {
      sendOfflineBeacon();
    };

    const handlePageHide = () => {
      sendOfflineBeacon();
    };

    // Don't set offline on visibility change — keep user "present"
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        updatePresence(uid, true);
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("pagehide", handlePageHide);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      if (heartbeatRef.current) clearInterval(heartbeatRef.current);
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("pagehide", handlePageHide);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      if (!presenceCleanupDone.current) {
        presenceCleanupDone.current = true;
        updatePresence(uid, false);
      }
    };
  }, [user?.id, updatePresence]);

  const signOut = async () => {
    const userId = user?.id;

    if (userId) {
      dispatchChatUserLogout(userId);
    }

    markGuestManualLogout();
    writeCachedAuth(null);
    clearStoredGuestName();
    try { localStorage.removeItem("chat-boot-cache"); } catch {}
    setSession(null);
    setUser(null);
    setLoading(false);

    if (!userId) {
      await supabase.auth.signOut();
      return;
    }

    await Promise.allSettled([
      broadcastChatUserLogout(userId),
      updatePresence(userId, false),
      supabase.auth.signOut(),
    ]);
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};
